import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Users, Plus, Trash2, Search, Pencil, UserPlus,
  Calendar, Clock, MapPin, CheckCircle, XCircle,
  FileText, AlertTriangle, UserCheck, Briefcase,
  CalendarDays, Timer, ClipboardList, Eye, Shield
} from 'lucide-react';
import { formatCurrency } from '../utils/exportHelpers';

export default function HumanResources() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  // State
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [permissionRequests, setPermissionRequests] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('employees');
  
  // Dialog states
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [attendanceEditDialogOpen, setAttendanceEditDialogOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [attendanceCorrectionForm, setAttendanceCorrectionForm] = useState({
    check_in_time: '', check_out_time: '', status: 'present', correction_reason: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Forms
  const [newEmployee, setNewEmployee] = useState({
    employee_code: '',
    employee_name_ar: '',
    employee_name_en: '',
    national_id: '',
    phone: '',
    email: '',
    department: '',
    job_title: '',
    hire_date: '',
    salary: 0,
    employee_type: 'full_time',
    manager_code: '',
    work_location_lat: null,
    work_location_lng: null,
    work_radius: 100,
    is_manager: false,
    attendance_mandatory: true,
    is_active: true,
    username: '',
    password: ''
  });
  
  const [newLeave, setNewLeave] = useState({
    employee_code: '',
    leave_type_code: '',
    start_date: '',
    end_date: '',
    days_count: 0,
    reason: ''
  });
  
  const [newPermission, setNewPermission] = useState({
    employee_code: '',
    permission_date: '',
    start_time: '',
    end_time: '',
    hours_count: 0,
    reason: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [empRes, leaveTypesRes, leavesRes, permRes, attRes, statsRes] = await Promise.all([
        api.get('/hr/employees'),
        api.get('/hr/leave-types'),
        api.get('/hr/leave-requests'),
        api.get('/hr/permission-requests'),
        api.get('/hr/attendance'),
        api.get('/hr/stats')
      ]);
      setEmployees(empRes.data);
      setLeaveTypes(leaveTypesRes.data);
      setLeaveRequests(leavesRes.data);
      setPermissionRequests(permRes.data);
      setAttendance(attRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const loadNextEmployeeCode = async () => {
    try {
      const res = await api.get('/hr/employees/next-code');
      setNewEmployee(prev => ({ ...prev, employee_code: res.data.next_code }));
    } catch (error) {
      console.error('Error loading next code');
    }
  };

  // Employee CRUD
  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    if (!newEmployee.employee_code || !newEmployee.employee_name_ar) {
      toast.error('يرجى ملء الحقول المطلوبة');
      return;
    }
    
    try {
      if (editMode && editingEmployee) {
        await api.put(`/hr/employees/${editingEmployee}`, newEmployee);
        toast.success('تم تحديث بيانات الموظف بنجاح');
      } else {
        await api.post('/hr/employees', newEmployee);
        toast.success('تم إضافة الموظف بنجاح');
      }
      setEmployeeDialogOpen(false);
      resetEmployeeForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const editEmployee = (emp) => {
    setNewEmployee({
      ...emp,
      password: '',
      permissions: emp.permissions || {
        can_check_in: true,
        can_request_leave: true,
        can_request_permission: true,
        can_view_salary: false,
        can_view_balance: true,
        can_view_attendance: true,
        can_approve_requests: false,
        can_custody_settlement: false,
      }
    });
    setEditMode(true);
    setEditingEmployee(emp.employee_code);
    setEmployeeDialogOpen(true);
  };

  const deleteEmployee = async (empCode) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الموظف؟')) return;
    try {
      await api.delete(`/hr/employees/${empCode}`);
      toast.success('تم حذف الموظف بنجاح');
      loadData();
    } catch (error) {
      toast.error('حدث خطأ أثناء الحذف');
    }
  };

  const resetEmployeeForm = () => {
    setNewEmployee({
      employee_code: '',
      employee_name_ar: '',
      employee_name_en: '',
      national_id: '',
      phone: '',
      email: '',
      department: '',
      job_title: '',
      hire_date: '',
      salary: 0,
      employee_type: 'full_time',
      manager_code: '',
      work_location_lat: null,
      work_location_lng: null,
      work_radius: 100,
      is_manager: false,
      attendance_mandatory: true,
      is_active: true,
      username: '',
      password: '',
      permissions: {
        can_check_in: true,
        can_request_leave: true,
        can_request_permission: true,
        can_view_salary: false,
        can_view_balance: true,
        can_view_attendance: true,
        can_approve_requests: false,
        can_custody_settlement: false,
      }
    });
    setEditMode(false);
    setEditingEmployee(null);
  };

  // Leave Request
  const handleSaveLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/leave-requests', {
        ...newLeave,
        request_id: `LR-${Date.now()}`
      });
      toast.success('تم تقديم طلب الإجازة بنجاح');
      setLeaveDialogOpen(false);
      setNewLeave({ employee_code: '', leave_type_code: '', start_date: '', end_date: '', days_count: 0, reason: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const approveLeave = async (requestId) => {
    try {
      await api.put(`/hr/leave-requests/${requestId}/approve?approved_by=admin`);
      toast.success('تمت الموافقة على الإجازة');
      loadData();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const rejectLeave = async (requestId) => {
    const reason = prompt('سبب الرفض:');
    try {
      await api.put(`/hr/leave-requests/${requestId}/reject?rejected_by=admin&reason=${reason || ''}`);
      toast.success('تم رفض الإجازة');
      loadData();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  // Permission Request
  const handleSavePermission = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/permission-requests', {
        ...newPermission,
        request_id: `PR-${Date.now()}`
      });
      toast.success('تم تقديم طلب الاستئذان بنجاح');
      setPermissionDialogOpen(false);
      setNewPermission({ employee_code: '', permission_date: '', start_time: '', end_time: '', hours_count: 0, reason: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const approvePermission = async (requestId) => {
    try {
      await api.put(`/hr/permission-requests/${requestId}/approve?approved_by=admin`);
      toast.success('تمت الموافقة على الاستئذان');
      loadData();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const rejectPermission = async (requestId) => {
    try {
      await api.put(`/hr/permission-requests/${requestId}/reject?rejected_by=admin`);
      toast.success('تم رفض الاستئذان');
      loadData();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  // View Employee Report
  const viewEmployeeReport = async (empCode) => {
    try {
      const res = await api.get(`/hr/employee-report/${empCode}`);
      setSelectedReport(res.data);
      setReportDialogOpen(true);
    } catch (error) {
      toast.error('حدث خطأ في تحميل التقرير');
    }
  };

  // Calculate days
  const calculateDays = () => {
    if (newLeave.start_date && newLeave.end_date) {
      const start = new Date(newLeave.start_date);
      const end = new Date(newLeave.end_date);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      setNewLeave(prev => ({ ...prev, days_count: days > 0 ? days : 0 }));
    }
  };

  useEffect(() => {
    calculateDays();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount
  }, [newLeave.start_date, newLeave.end_date]);

  // Filtering
  const filteredEmployees = employees.filter(emp => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      emp.employee_name_ar?.toLowerCase().includes(term) ||
      emp.employee_code?.toLowerCase().includes(term) ||
      emp.department?.toLowerCase().includes(term) ||
      emp.job_title?.toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-500/20 text-yellow-600">في الانتظار</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-500/20 text-green-600">موافق عليه</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-500/20 text-red-600">مرفوض</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getEmployeeName = (code) => {
    const emp = employees.find(e => e.employee_code === code);
    return emp?.employee_name_ar || code;
  };

  const openAttendanceEdit = (att) => {
    setEditingAttendance(att);
    setAttendanceCorrectionForm({
      check_in_time: att.check_in_time || '',
      check_out_time: att.check_out_time || '',
      status: att.status || 'present',
      correction_reason: ''
    });
    setAttendanceEditDialogOpen(true);
  };

  const normalizeTime = (t) => {
    if (!t) return '';
    return t.split(':').length === 2 ? `${t}:00` : t;
  };

  const handleSaveAttendanceCorrection = async () => {
    if (!attendanceCorrectionForm.correction_reason.trim()) {
      toast.error('الرجاء كتابة سبب التصحيح للمراجعة');
      return;
    }
    try {
      await api.put(`/hr/attendance/${editingAttendance.attendance_id}/correct`, {
        ...attendanceCorrectionForm,
        check_in_time: normalizeTime(attendanceCorrectionForm.check_in_time),
        check_out_time: normalizeTime(attendanceCorrectionForm.check_out_time),
      });
      toast.success('تم تصحيح وقت البصمة بنجاح');
      setAttendanceEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ أثناء التصحيح');
    }
  };

  const getLeaveTypeName = (code) => {
    const lt = leaveTypes.find(l => l.leave_type_code === code);
    return lt?.leave_type_name_ar || code;
  };

  if (loading) return <div className="text-center py-12">جاري التحميل...</div>;

  return (
    <div className="space-y-6" data-testid="hr-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-7 w-7 text-emerald" />
            الموارد البشرية
          </h1>
          <p className="text-muted-foreground text-sm mt-1">إدارة الموظفين والإجازات والحضور</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-leave-button">
                <CalendarDays className="h-4 w-4 me-2" />طلب إجازة
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>طلب إجازة جديد</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveLeave} className="space-y-4">
                <div>
                  <Label>الموظف *</Label>
                  <select
                    value={newLeave.employee_code}
                    onChange={(e) => setNewLeave({...newLeave, employee_code: e.target.value})}
                    className="w-full h-10 px-3 border rounded-md bg-background"
                    required
                  >
                    <option value="">-- اختر الموظف --</option>
                    {employees.map(emp => (
                      <option key={emp.employee_code} value={emp.employee_code}>
                        {emp.employee_name_ar}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>نوع الإجازة *</Label>
                  <select
                    value={newLeave.leave_type_code}
                    onChange={(e) => setNewLeave({...newLeave, leave_type_code: e.target.value})}
                    className="w-full h-10 px-3 border rounded-md bg-background"
                    required
                  >
                    <option value="">-- اختر النوع --</option>
                    {leaveTypes.map(lt => (
                      <option key={lt.leave_type_code} value={lt.leave_type_code}>
                        {lt.leave_type_name_ar}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>من تاريخ *</Label>
                    <Input
                      type="date"
                      value={newLeave.start_date}
                      onChange={(e) => setNewLeave({...newLeave, start_date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>إلى تاريخ *</Label>
                    <Input
                      type="date"
                      value={newLeave.end_date}
                      onChange={(e) => setNewLeave({...newLeave, end_date: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>عدد الأيام</Label>
                  <Input value={newLeave.days_count} disabled className="bg-muted" />
                </div>
                <div>
                  <Label>السبب</Label>
                  <textarea
                    value={newLeave.reason}
                    onChange={(e) => setNewLeave({...newLeave, reason: e.target.value})}
                    className="w-full h-20 px-3 py-2 border rounded-md bg-background resize-none"
                    placeholder="سبب الإجازة..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setLeaveDialogOpen(false)}>إلغاء</Button>
                  <Button type="submit" className="bg-emerald hover:bg-emerald-hover">تقديم الطلب</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-permission-button">
                <Timer className="h-4 w-4 me-2" />طلب استئذان
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>طلب استئذان جديد</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSavePermission} className="space-y-4">
                <div>
                  <Label>الموظف *</Label>
                  <select
                    value={newPermission.employee_code}
                    onChange={(e) => setNewPermission({...newPermission, employee_code: e.target.value})}
                    className="w-full h-10 px-3 border rounded-md bg-background"
                    required
                  >
                    <option value="">-- اختر الموظف --</option>
                    {employees.map(emp => (
                      <option key={emp.employee_code} value={emp.employee_code}>
                        {emp.employee_name_ar}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>التاريخ *</Label>
                  <Input
                    type="date"
                    value={newPermission.permission_date}
                    onChange={(e) => setNewPermission({...newPermission, permission_date: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>من الساعة *</Label>
                    <Input
                      type="time"
                      value={newPermission.start_time}
                      onChange={(e) => setNewPermission({...newPermission, start_time: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>إلى الساعة *</Label>
                    <Input
                      type="time"
                      value={newPermission.end_time}
                      onChange={(e) => setNewPermission({...newPermission, end_time: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>السبب</Label>
                  <textarea
                    value={newPermission.reason}
                    onChange={(e) => setNewPermission({...newPermission, reason: e.target.value})}
                    className="w-full h-20 px-3 py-2 border rounded-md bg-background resize-none"
                    placeholder="سبب الاستئذان..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setPermissionDialogOpen(false)}>إلغاء</Button>
                  <Button type="submit" className="bg-emerald hover:bg-emerald-hover">تقديم الطلب</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={employeeDialogOpen} onOpenChange={(open) => {
            setEmployeeDialogOpen(open);
            if (open && !editMode) loadNextEmployeeCode();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald hover:bg-emerald-hover" onClick={resetEmployeeForm} data-testid="add-employee-button">
                <UserPlus className="h-4 w-4 me-2" />إضافة موظف
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editMode ? 'تعديل بيانات الموظف' : 'إضافة موظف جديد'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveEmployee} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>كود الموظف *</Label>
                    <Input
                      value={newEmployee.employee_code}
                      onChange={(e) => setNewEmployee({...newEmployee, employee_code: e.target.value})}
                      required
                      disabled={editMode}
                    />
                  </div>
                  <div>
                    <Label>الاسم بالعربية *</Label>
                    <Input
                      value={newEmployee.employee_name_ar}
                      onChange={(e) => setNewEmployee({...newEmployee, employee_name_ar: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>الاسم بالإنجليزية</Label>
                    <Input
                      value={newEmployee.employee_name_en}
                      onChange={(e) => setNewEmployee({...newEmployee, employee_name_en: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>رقم الهوية</Label>
                    <Input
                      value={newEmployee.national_id}
                      onChange={(e) => setNewEmployee({...newEmployee, national_id: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>رقم الجوال</Label>
                    <Input
                      value={newEmployee.phone}
                      onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>البريد الإلكتروني</Label>
                    <Input
                      type="email"
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>القسم</Label>
                    <Input
                      value={newEmployee.department}
                      onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})}
                      placeholder="مثال: المالية"
                    />
                  </div>
                  <div>
                    <Label>المسمى الوظيفي</Label>
                    <Input
                      value={newEmployee.job_title}
                      onChange={(e) => setNewEmployee({...newEmployee, job_title: e.target.value})}
                      placeholder="مثال: محاسب"
                    />
                  </div>
                  <div>
                    <Label>تاريخ التعيين</Label>
                    <Input
                      type="date"
                      value={newEmployee.hire_date}
                      onChange={(e) => setNewEmployee({...newEmployee, hire_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>الراتب</Label>
                    <Input
                      type="number"
                      value={newEmployee.salary}
                      onChange={(e) => setNewEmployee({...newEmployee, salary: parseFloat(e.target.value) || 0})}
                      className="text-end"
                    />
                  </div>
                  <div>
                    <Label>نوع التوظيف</Label>
                    <select
                      value={newEmployee.employee_type}
                      onChange={(e) => setNewEmployee({...newEmployee, employee_type: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md bg-background"
                    >
                      <option value="full_time">دوام كامل</option>
                      <option value="part_time">دوام جزئي</option>
                      <option value="contract">عقد مؤقت</option>
                    </select>
                  </div>
                  <div>
                    <Label>المدير المباشر</Label>
                    <select
                      value={newEmployee.manager_code || ''}
                      onChange={(e) => setNewEmployee({...newEmployee, manager_code: e.target.value})}
                      className="w-full h-10 px-3 border rounded-md bg-background"
                    >
                      <option value="">-- بدون مدير --</option>
                      {employees.filter(e => e.is_manager).map(emp => (
                        <option key={emp.employee_code} value={emp.employee_code}>
                          {emp.employee_name_ar}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    موقع العمل (للبصمة)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>خط العرض (Latitude)</Label>
                      <Input
                        type="number"
                        step="any"
                        value={newEmployee.work_location_lat || ''}
                        onChange={(e) => setNewEmployee({...newEmployee, work_location_lat: parseFloat(e.target.value) || null})}
                        placeholder="24.7136"
                      />
                    </div>
                    <div>
                      <Label>خط الطول (Longitude)</Label>
                      <Input
                        type="number"
                        step="any"
                        value={newEmployee.work_location_lng || ''}
                        onChange={(e) => setNewEmployee({...newEmployee, work_location_lng: parseFloat(e.target.value) || null})}
                        placeholder="46.6753"
                      />
                    </div>
                    <div>
                      <Label>نطاق البصمة (متر)</Label>
                      <Input
                        type="number"
                        value={newEmployee.work_radius}
                        onChange={(e) => setNewEmployee({...newEmployee, work_radius: parseFloat(e.target.value) || 100})}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">بيانات الدخول (بوابة الموظف)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>اسم المستخدم</Label>
                      <Input
                        value={newEmployee.username}
                        onChange={(e) => setNewEmployee({...newEmployee, username: e.target.value})}
                        placeholder="للدخول على بوابة الموظفين"
                      />
                    </div>
                    <div>
                      <Label>كلمة المرور</Label>
                      <Input
                        type="password"
                        value={newEmployee.password}
                        onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                        placeholder={editMode ? "اتركها فارغة للإبقاء على الحالية" : ""}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newEmployee.is_manager}
                      onChange={(e) => setNewEmployee({...newEmployee, is_manager: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">مدير (صلاحيات إدارية)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newEmployee.attendance_mandatory}
                      onChange={(e) => setNewEmployee({...newEmployee, attendance_mandatory: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm" title="لو ألغيت التحديد، لن يُخصم على هذا الموظف أي غياب أو تأخير حتى لو لم يبصم أبدًا (مناسب للرئيس/المدير غير الملزمين بالتبصيم)">
                      ملزم بتسجيل الحضور (البصمة)
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newEmployee.is_active}
                      onChange={(e) => setNewEmployee({...newEmployee, is_active: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">نشط</span>
                  </label>
                </div>

                {/* صلاحيات الموظف */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-purple-500" />
                    صلاحيات بوابة الموظف
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_check_in !== false}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_check_in: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">البصمة</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_request_leave !== false}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_request_leave: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">طلب إجازة</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_request_permission !== false}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_request_permission: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">طلب استئذان</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_view_balance !== false}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_view_balance: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">رؤية الرصيد</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_view_attendance !== false}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_view_attendance: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">رؤية السجل</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_view_salary === true}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_view_salary: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">رؤية الراتب</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_approve_requests === true}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_approve_requests: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">الموافقة على الطلبات</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-lg hover:bg-muted/50">
                      <input
                        type="checkbox"
                        checked={newEmployee.permissions?.can_custody_settlement === true}
                        onChange={(e) => setNewEmployee({
                          ...newEmployee, 
                          permissions: {...(newEmployee.permissions || {}), can_custody_settlement: e.target.checked}
                        })}
                        className="rounded"
                      />
                      <span className="text-sm">تصفية العهدة (تصوير الفواتير وإرسالها للاعتماد)</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setEmployeeDialogOpen(false)}>إلغاء</Button>
                  <Button type="submit" className="bg-emerald hover:bg-emerald-hover">{editMode ? 'تحديث' : 'حفظ'}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald/10 rounded-lg">
                <Users className="h-5 w-5 text-emerald" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">إجمالي الموظفين</p>
                <p className="text-xl font-bold">{stats.total_employees}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <UserCheck className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">حاضرين اليوم</p>
                <p className="text-xl font-bold text-green-500">{stats.present_today}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-lg">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">غائبين</p>
                <p className="text-xl font-bold text-red-500">{stats.absent_today}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">في إجازة</p>
                <p className="text-xl font-bold text-blue-500">{stats.on_leave_today}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <CalendarDays className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">طلبات إجازة معلقة</p>
                <p className="text-xl font-bold text-yellow-500">{stats.pending_leave_requests}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Timer className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">طلبات استئذان معلقة</p>
                <p className="text-xl font-bold text-purple-500">{stats.pending_permission_requests}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <Users className="h-4 w-4" />الموظفين
          </TabsTrigger>
          <TabsTrigger value="leaves" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />الإجازات
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex items-center gap-2">
            <Timer className="h-4 w-4" />الاستئذان
          </TabsTrigger>
          <TabsTrigger value="attendance" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />الحضور
          </TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees" className="mt-4">
          <Card className="p-4 mb-4">
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="بحث بالاسم أو الكود أو القسم..."
                className="ps-10"
              />
            </div>
          </Card>
          
          <Card className="border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-start">الكود</th>
                    <th className="px-4 py-3 text-start">الاسم</th>
                    <th className="px-4 py-3 text-start">القسم</th>
                    <th className="px-4 py-3 text-start">المسمى الوظيفي</th>
                    <th className="px-4 py-3 text-start">نوع التوظيف</th>
                    <th className="px-4 py-3 text-start">الحالة</th>
                    <th className="px-4 py-3 text-start">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => (
                    <tr key={emp.employee_code} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-sm">{emp.employee_code}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{emp.employee_name_ar}</p>
                          {emp.employee_name_en && <p className="text-xs text-muted-foreground">{emp.employee_name_en}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{emp.department || '-'}</td>
                      <td className="px-4 py-3 text-sm">{emp.job_title || '-'}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {emp.employee_type === 'full_time' ? 'دوام كامل' : emp.employee_type === 'part_time' ? 'دوام جزئي' : 'عقد'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {emp.is_active ? (
                          <Badge variant="outline" className="bg-green-500/20 text-green-600 text-xs">نشط</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">غير نشط</Badge>
                        )}
                        {emp.is_manager && <Badge className="ms-1 bg-purple-500 text-xs">مدير</Badge>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => viewEmployeeReport(emp.employee_code)} title="تقرير">
                            <Eye className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => editEmployee(emp)} title="تعديل">
                            <Pencil className="h-4 w-4 text-emerald" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => deleteEmployee(emp.employee_code)} title="حذف">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEmployees.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>لا يوجد موظفين</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Leaves Tab */}
        <TabsContent value="leaves" className="mt-4">
          <Card className="border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-start">رقم الطلب</th>
                    <th className="px-4 py-3 text-start">الموظف</th>
                    <th className="px-4 py-3 text-start">نوع الإجازة</th>
                    <th className="px-4 py-3 text-start">من</th>
                    <th className="px-4 py-3 text-start">إلى</th>
                    <th className="px-4 py-3 text-center">الأيام</th>
                    <th className="px-4 py-3 text-start">الحالة</th>
                    <th className="px-4 py-3 text-start">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveRequests.map((req) => (
                    <tr key={req.request_id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-sm">{req.request_id}</td>
                      <td className="px-4 py-3">{getEmployeeName(req.employee_code)}</td>
                      <td className="px-4 py-3">{getLeaveTypeName(req.leave_type_code)}</td>
                      <td className="px-4 py-3 text-sm">{req.start_date}</td>
                      <td className="px-4 py-3 text-sm">{req.end_date}</td>
                      <td className="px-4 py-3 text-center font-semibold">{req.days_count}</td>
                      <td className="px-4 py-3">{getStatusBadge(req.status)}</td>
                      <td className="px-4 py-3">
                        {req.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => approveLeave(req.request_id)} className="text-green-600 hover:text-green-700">
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => rejectLeave(req.request_id)} className="text-red-600 hover:text-red-700">
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leaveRequests.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد طلبات إجازات</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="mt-4">
          <Card className="border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-start">رقم الطلب</th>
                    <th className="px-4 py-3 text-start">الموظف</th>
                    <th className="px-4 py-3 text-start">التاريخ</th>
                    <th className="px-4 py-3 text-start">من</th>
                    <th className="px-4 py-3 text-start">إلى</th>
                    <th className="px-4 py-3 text-start">السبب</th>
                    <th className="px-4 py-3 text-start">الحالة</th>
                    <th className="px-4 py-3 text-start">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {permissionRequests.map((req) => (
                    <tr key={req.request_id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-sm">{req.request_id}</td>
                      <td className="px-4 py-3">{getEmployeeName(req.employee_code)}</td>
                      <td className="px-4 py-3 text-sm">{req.permission_date}</td>
                      <td className="px-4 py-3 text-sm">{req.start_time}</td>
                      <td className="px-4 py-3 text-sm">{req.end_time}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{req.reason || '-'}</td>
                      <td className="px-4 py-3">{getStatusBadge(req.status)}</td>
                      <td className="px-4 py-3">
                        {req.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => approvePermission(req.request_id)} className="text-green-600">
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => rejectPermission(req.request_id)} className="text-red-600">
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {permissionRequests.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Timer className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد طلبات استئذان</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="mt-4">
          <Card className="border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-start">التاريخ</th>
                    <th className="px-4 py-3 text-start">الموظف</th>
                    <th className="px-4 py-3 text-start">وقت الحضور</th>
                    <th className="px-4 py-3 text-start">وقت الانصراف</th>
                    <th className="px-4 py-3 text-center">ساعات العمل</th>
                    <th className="px-4 py-3 text-start">الحالة</th>
                    <th className="px-4 py-3 text-start">الموقع</th>
                    <th className="px-4 py-3 text-start"></th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((att) => (
                    <tr key={att.attendance_id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">{att.attendance_date}</td>
                      <td className="px-4 py-3">{getEmployeeName(att.employee_code)}</td>
                      <td className="px-4 py-3 text-sm font-mono">
                        {att.check_in_time || '-'}
                        {att.manually_corrected && <Badge variant="outline" className="ms-1 text-xs bg-blue-500/10 text-blue-600">مصحّح</Badge>}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono">{att.check_out_time || '-'}</td>
                      <td className="px-4 py-3 text-center font-semibold">{att.work_hours || 0} ساعة</td>
                      <td className="px-4 py-3">
                        {att.status === 'present' ? (
                          <Badge variant="outline" className="bg-green-500/20 text-green-600">حاضر</Badge>
                        ) : att.status === 'late' ? (
                          <Badge variant="outline" className="bg-yellow-500/20 text-yellow-600">متأخر</Badge>
                        ) : (
                          <Badge variant="secondary">{att.status}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {att.check_in_location_valid ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button size="sm" variant="ghost" onClick={() => openAttendanceEdit(att)} title="تصحيح وقت البصمة">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {attendance.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>لا توجد سجلات حضور</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>تقرير الموظف</DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-6">
              {/* Employee Info */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  معلومات الموظف
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><span className="text-muted-foreground">الكود:</span> {selectedReport.employee.employee_code}</div>
                  <div><span className="text-muted-foreground">الاسم:</span> {selectedReport.employee.employee_name_ar}</div>
                  <div><span className="text-muted-foreground">القسم:</span> {selectedReport.employee.department || '-'}</div>
                  <div><span className="text-muted-foreground">المسمى:</span> {selectedReport.employee.job_title || '-'}</div>
                  <div><span className="text-muted-foreground">الجوال:</span> {selectedReport.employee.phone || '-'}</div>
                  <div><span className="text-muted-foreground">البريد:</span> {selectedReport.employee.email || '-'}</div>
                </div>
              </Card>

              {/* Leave Balances */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  رصيد الإجازات
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {selectedReport.leave_balances.map((bal) => (
                    <div key={bal.leave_type_code} className="p-3 border rounded-lg">
                      <p className="text-sm text-muted-foreground">{getLeaveTypeName(bal.leave_type_code)}</p>
                      <p className="text-lg font-bold">{bal.remaining_days} <span className="text-sm font-normal">/ {bal.total_days}</span></p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Work Hours This Month */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  ساعات العمل هذا الشهر
                </h3>
                <p className="text-3xl font-bold text-emerald">{selectedReport.total_work_hours_this_month} <span className="text-lg font-normal">ساعة</span></p>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attendance Correction Dialog */}
      <Dialog open={attendanceEditDialogOpen} onOpenChange={setAttendanceEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              تصحيح وقت البصمة
            </DialogTitle>
            <DialogDescription>
              {editingAttendance && `${getEmployeeName(editingAttendance.employee_code)} - ${editingAttendance.attendance_date}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>وقت الحضور</Label>
                <Input
                  type="time"
                  step="1"
                  value={attendanceCorrectionForm.check_in_time}
                  onChange={(e) => setAttendanceCorrectionForm({...attendanceCorrectionForm, check_in_time: e.target.value})}
                />
              </div>
              <div>
                <Label>وقت الانصراف</Label>
                <Input
                  type="time"
                  step="1"
                  value={attendanceCorrectionForm.check_out_time}
                  onChange={(e) => setAttendanceCorrectionForm({...attendanceCorrectionForm, check_out_time: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>الحالة</Label>
              <select
                value={attendanceCorrectionForm.status}
                onChange={(e) => setAttendanceCorrectionForm({...attendanceCorrectionForm, status: e.target.value})}
                className="w-full h-10 px-3 border rounded-lg bg-white"
              >
                <option value="present">حاضر</option>
                <option value="late">متأخر</option>
                <option value="absent">غائب</option>
              </select>
            </div>
            <div>
              <Label>سبب التصحيح *</Label>
              <textarea
                value={attendanceCorrectionForm.correction_reason}
                onChange={(e) => setAttendanceCorrectionForm({...attendanceCorrectionForm, correction_reason: e.target.value})}
                placeholder="مثال: خطأ بتوقيت جهاز الموظف، نسي يبصم وحضر فعليًا..."
                className="w-full h-20 px-3 py-2 border rounded-lg bg-white resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">يُسجَّل هذا السبب مع كل تصحيح للمراجعة لاحقًا</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAttendanceEditDialogOpen(false)}>إلغاء</Button>
              <Button onClick={handleSaveAttendanceCorrection}>حفظ التصحيح</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
